import { Formik } from 'formik'
import React from 'react'
import useFilters from '../../hooks/filters'
import {
  FiltersResourcesEnum,
  WorkPeriodsFilters as StoreWorkPeriodsFilters,
} from '../../store/filters'
import AutoSubmit from '../form/utils/auto-submit'
import Protected from '../protected/protected'
import WorkersFilterField from '../filters/fields/workers'
import OrganizationFilterField from '../filters/fields/organization'
import { OrganizationTypeEnum } from '../../api/organizations'
import JobTitleFilterField from '../filters/fields/job-title'
import IsCancelledFilterField from '../filters/fields/is-cancelled'
import DateFilterField from '../filters/fields/date'
import { Columns } from 'react-bulma-components'
import IsAbandonedFilterField from '../filters/fields/is-abandoned'
import IsValidatedFilterField from '../filters/fields/is-validated'
import ServiceFilterField from '../filters/fields/service'
import FiltersWrapper from '../filters/filters-wrapper'
import { AppUserRole } from '../../store/session'
import EventsFilterField from '../filters/fields/events'
import WorkPeriodTagsFilterField from '../filters/fields/work-period-tags'

type FilterConfig = {
  key: keyof StoreWorkPeriodsFilters
  component: React.ReactNode
  roles?: AppUserRole[]
  defaultValue: any
}

export const WorkPeriodsFilters: React.FC<{
  display?: Array<keyof StoreWorkPeriodsFilters> | false
}> = ({ display }) => {
  const { setFilters, filters } = useFilters(FiltersResourcesEnum.workPeriods)
  if (display === false) return <></>

  const queriesStaleTime = 1000 * 60 * 2 // 2 min

  const filterConfigs: FilterConfig[] = [
    {
      key: 'start',
      component: <DateFilterField name="start" label="Date" />,
      defaultValue: filters.start,
    },
    {
      key: 'user',
      component: <WorkersFilterField />,
      roles: ['employerMember', 'interimAgencyMember'],
      defaultValue: filters.user || [],
    },
    {
      key: 'interimAgency',
      component: (
        <OrganizationFilterField
          queriesStaleTime={queriesStaleTime}
          type={OrganizationTypeEnum.interimAgency}
        />
      ),
      roles: ['employerMember'],
      defaultValue: filters.interimAgency || [],
    },
    {
      key: 'employer',
      component: (
        <OrganizationFilterField
          queriesStaleTime={queriesStaleTime}
          type={OrganizationTypeEnum.employer}
        />
      ),
      roles: ['interimAgencyMember'],
      defaultValue: filters.employer || [],
    },
    {
      key: 'jobTitle',
      component: (
        <JobTitleFilterField queriesStaleTime={queriesStaleTime} employerIds={filters.employer} />
      ),
      roles: ['employerMember'],
      defaultValue: filters.jobTitle || [],
    },
    {
      key: 'service',
      component: (
        <ServiceFilterField queriesStaleTime={queriesStaleTime} employerIds={filters.employer} />
      ),
      roles: ['employerMember'],
      defaultValue: filters.service || [],
    },
    {
      key: 'events',
      component: <EventsFilterField />,
      roles: ['employerMember', 'interimAgencyMember'],
      defaultValue: filters.events || [],
    },
    {
      key: 'tags',
      component: (
        <WorkPeriodTagsFilterField
          queriesStaleTime={queriesStaleTime}
        />
      ),
      roles: ['employerMember'],
      defaultValue: filters.tags || [],
    },
    {
      key: 'isCancelled',
      component: <IsCancelledFilterField />,
      roles: ['employerMember', 'interimAgencyMember'],
      defaultValue: filters.isCancelled,
    },
    {
      key: 'isAbandoned',
      component: <IsAbandonedFilterField />,
      roles: ['employerMember', 'interimAgencyMember'],
      defaultValue: filters.isAbandoned,
    },
    {
      key: 'isValidated',
      component: <IsValidatedFilterField />,
      roles: ['employerMember', 'interimAgencyMember'],
      defaultValue: filters.isValidated,
    },
  ]

  const initialValues = filterConfigs.reduce(
    (acc, { key, defaultValue }) => ({
      ...acc,
      [key]: defaultValue,
      end: filters.end,
    }),
    {} as StoreWorkPeriodsFilters,
  )

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={values => {
        setFilters(FiltersResourcesEnum.workPeriods, values)
      }}
    >
      {props => (
        <FiltersWrapper resource={FiltersResourcesEnum.workPeriods}>
          <Columns gap={3}>
            {filterConfigs.map(({ key, component, roles }) => {
              const isDisplayed = display?.includes(key) ?? true
              if (!isDisplayed) return null

              const filterComponent = roles ? (
                <Protected roles={roles}>{component}</Protected>
              ) : (
                component
              )

              return <React.Fragment key={key}>{filterComponent}</React.Fragment>
            })}
            <AutoSubmit values={props.values} submitForm={props.submitForm} />
          </Columns>
        </FiltersWrapper>
      )}
    </Formik>
  )
}
